import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";
import axios from "axios";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const SESSION_TIMEOUT = 15000;
const readStoredToken = () => {
  try {
    const token = JSON.parse(localStorage.getItem("token"));
    return typeof token === "string" && token ? token : null;
  } catch { return null; }
};
const isAuthRequest = config => /\/auth\/(refresh_token|login|logout|signup)(?:[/?]|$)/.test(config?.url || "");
const isExpiredAccessToken = error => error.response?.status === 403 &&
  ["Invalid token. We'll try to assign a new one on next request", "ERR_INVALID_TOKEN", "ERR_SESSION_EXPIRED"].includes(error.response?.data?.error);

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState({});
  const [startupError, setStartupError] = useState(null);
  const refreshRequest = useRef(null), restoreVersion = useRef(0), mounted = useRef(true);
  const sessionGeneration = useRef(0);
  const invalidateSession = useCallback(() => {
    sessionGeneration.current++;
    const pending = refreshRequest.current;
    refreshRequest.current = null;
    if (pending) pending.cancel.cancel("Session changed");
  }, []);
  const clearAuth = useCallback(() => {
    invalidateSession();
    localStorage.removeItem("token");
    delete api.defaults.headers.Authorization;
    setIsAuth(false); setUser({});
  }, [invalidateSession]);
  const acceptSession = useCallback(data => {
    localStorage.setItem("token", JSON.stringify(data.token));
    api.defaults.headers.Authorization = `Bearer ${data.token}`;
    setUser(data.user); setIsAuth(true);
  }, []);
  const refreshSession = useCallback(() => {
    if (refreshRequest.current) return refreshRequest.current.promise;
    const generation = sessionGeneration.current;
    const cancel = axios.CancelToken.source();
    let timer;
    // Bound the full interceptor chain as well as the HTTP transport.
    const promise = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        const error = new Error("Session request timed out");
        error.code = "ECONNABORTED";
        reject(error); cancel.cancel("Session request timed out");
      }, SESSION_TIMEOUT);
      api.post("/auth/refresh_token", undefined, { timeout: SESSION_TIMEOUT, cancelToken: cancel.token }).then(resolve, reject);
    }).then(({ data }) => {
      if (generation !== sessionGeneration.current) throw new axios.Cancel("Session changed");
      if (typeof data?.token !== "string" || !data.token || !data?.user?.id || !data.user.companyId) throw new Error("Invalid session response");
      return data;
    }).finally(() => {
      clearTimeout(timer);
      if (refreshRequest.current?.promise === promise) refreshRequest.current = null;
    });
    refreshRequest.current = { promise, cancel };
    return promise;
  }, []);
  useEffect(() => {
    mounted.current = true;
    const requestId = api.interceptors.request.use(config => {
      config._authGeneration = sessionGeneration.current;
      const token = readStoredToken();
      if (token) { config.headers = config.headers || {}; config.headers.Authorization = `Bearer ${token}`; }
      if (isAuthRequest(config) && !config.timeout) config.timeout = SESSION_TIMEOUT;
      return config;
    }, error => Promise.reject(error));
    const responseId = api.interceptors.response.use(response => response, async error => {
      const originalRequest = error.config;
      const generation = originalRequest?._authGeneration;
      if (!mounted.current || generation !== sessionGeneration.current) throw error;
      if (error.response?.status === 401 && mounted.current) clearAuth();
      if (!originalRequest || isAuthRequest(originalRequest) || !isExpiredAccessToken(error)) throw error;
      if (originalRequest._retry) { if (mounted.current) clearAuth(); throw error; }
      originalRequest._retry = true;
      try {
        const data = await refreshSession();
        if (!mounted.current || generation !== sessionGeneration.current) throw new axios.Cancel("Session changed");
        acceptSession(data);
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${data.token}` };
        return api(originalRequest);
      } catch (refreshError) {
        if (mounted.current && generation === sessionGeneration.current && [401, 403].includes(refreshError.response?.status)) clearAuth();
        throw refreshError;
      }
    });
    return () => {
      mounted.current = false;
      invalidateSession();
      api.interceptors.request.eject(requestId); api.interceptors.response.eject(responseId);
    };
  }, [acceptSession, clearAuth, refreshSession, invalidateSession]);
  const retrySession = useCallback(async () => {
    const version = ++restoreVersion.current;
    setLoading(true); setStartupError(null);
    if (!readStoredToken()) { clearAuth(); setLoading(false); return; }
    try {
      const data = await refreshSession();
      if (mounted.current && version === restoreVersion.current) acceptSession(data);
    } catch (error) {
      if (mounted.current && version === restoreVersion.current) {
        setIsAuth(false); setUser({});
        if ([401, 403].includes(error.response?.status)) clearAuth();
        else setStartupError(error.code === "ECONNABORTED" ? "O servidor demorou para responder. Tente novamente." : "Não foi possível conectar ao servidor. Tente novamente.");
      }
    } finally {
      if (mounted.current && version === restoreVersion.current) setLoading(false);
    }
  }, [acceptSession, clearAuth, refreshSession]);
  useEffect(() => { retrySession(); }, [retrySession]);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      // console.log("Entrou useWhatsapp com user", Object.keys(user).length, Object.keys(socket).length ,user, socket)
      let io;
      if (!Object.keys(socket).length || socket.disposed || Number(socket.companyId) !== Number(user.companyId) || Number(socket.userId) !== Number(user.id)) {
        io = socketConnection({ user });
        setSocket(io)
      } else {
        io = socket
      }
      io.on(`company-${user.companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      return () => {
        // console.log("desconectou o company user ", user.id)
        io.off(`company-${user.companyId}-user`);
        // io.disconnect();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [user]);

  const handleLogin = async (userData) => {
    invalidateSession(); restoreVersion.current++; setStartupError(null);
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null); //regra pra exibir campanhas
        }
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable); //regra pra exibir campanhas
        }
      }
      localStorage.setItem("profileImage", data.user.profileImage); //regra pra exibir imagem contato

      moment.locale('pt-br');
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = '2999-12-31T00:00:00.000Z'
      } else {
        dueDate = data.user.company.dueDate;
      }
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      var diff = moment(dueDate).diff(moment(moment()).format());

      var before = moment(moment().format()).isBefore(dueDate);
      var dias = moment.duration(diff).asDays();

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        // localStorage.setItem("public-token", JSON.stringify(data.user.token));
        // localStorage.setItem("companyId", companyId);
        // localStorage.setItem("userId", id);
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        if (Math.round(dias) < 5) {
          toast.warn(`Sua assinatura vence em ${Math.round(dias)} ${Math.round(dias) === 1 ? 'dia' : 'dias'} `);
        }

        // // Atraso para garantir que o cache foi limpo
        // setTimeout(() => {
        //   window.location.reload(true); // Recarregar a página
        // }, 1000);

        history.push("/tickets");
        setLoading(false);
      } else {
        // localStorage.setItem("companyId", companyId);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }

    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    invalidateSession(); restoreVersion.current++; setStartupError(null);
    setLoading(true);

    try {
      await api.delete("/auth/logout");
      if (typeof socket.disconnect === "function") socket.disconnect();
      // Keep the disposed worker callable until mounted consumers finish cleanup.
      clearAuth();
      localStorage.removeItem("cshow");
      // localStorage.removeItem("public-token");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data)
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    startupError,
    retrySession,
    socket
  };
};

export default useAuth;
